import React, { useEffect, useState } from "react";
import type { InvitePreview } from "../types";
import { claimInvite, fetchInvite } from "../authApi";
import { apiErrorMessage } from "../../utils/apiError";
import Spinner from "../../components/Spinner/Spinner";
import "./AcceptInvitePage.css";

interface Props {
  token: string;
}

const AcceptInvitePage: React.FC<Props> = ({ token }) => {
  const [preview, setPreview] = useState<InvitePreview | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchInvite(token)
      .then((p) => {
        if (cancelled) return;
        setPreview(p);
        if (p.invitee_name) setName(p.invitee_name);
      })
      .catch((err) => {
        if (cancelled) return;
        const status = err?.response?.data?.status;
        if (status === "claimed") {
          setLoadError("This invitation has already been used. Please log in.");
        } else if (status === "expired") {
          setLoadError("This invitation has expired. Ask your admin for a new one.");
        } else if (status === "revoked") {
          setLoadError("This invitation was cancelled. Ask your admin for a new one.");
        } else {
          setLoadError("This invitation link is not valid.");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);
    if (password !== confirm) {
      setSubmitError("Passwords don't match.");
      return;
    }
    setSubmitting(true);
    try {
      await claimInvite({ token, name: name.trim(), password });
      // Session cookie is now set; full reload lets AuthProvider pick it up.
      window.location.assign("/");
    } catch (err: any) {
      setSubmitError(apiErrorMessage(err, "Could not create your account."));
      setSubmitting(false);
    }
  };

  const goToLogin = () => window.location.assign("/");

  if (loading) {
    return (
      <div className="accept-page">
        <div className="accept-card">
          <Spinner visible label="Loading invitation…" />
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="accept-page">
        <div className="accept-card">
          <h1 className="accept-title">AutoCallahan</h1>
          <p className="accept-invalid">{loadError}</p>
          <button className="accept-btn" onClick={goToLogin}>
            Go to login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="accept-page">
      <form className="accept-card" onSubmit={handleSubmit}>
        <h1 className="accept-title">
          AutoCallahan
          <img
            src={process.env.PUBLIC_URL + "/callahan-logo.jpg"}
            alt="Callahan"
            className="accept-logo"
          />
        </h1>
        <p className="accept-subtitle">
          {preview?.inviter_name
            ? `${preview.inviter_name} invited you to join`
            : "You've been invited to join"}{" "}
          <strong>{preview?.tenant_display_name}</strong>
        </p>

        {submitError && <div className="accept-error">{submitError}</div>}

        <label className="accept-label">
          Email
          <input className="accept-input" value={preview?.email || ""} disabled />
        </label>

        <label className="accept-label">
          Your name
          <input
            className="accept-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoFocus
            required
          />
        </label>

        <label className="accept-label">
          Password
          <input
            className="accept-input"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
        </label>

        <label className="accept-label">
          Confirm password
          <input
            className="accept-input"
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            required
          />
        </label>

        <button
          className="accept-btn"
          type="submit"
          disabled={submitting || !name || !password || !confirm}
        >
          {submitting ? "Creating account…" : "Create account & sign in"}
        </button>
      </form>
    </div>
  );
};

export default AcceptInvitePage;
